const cachedItemPrefix = "ci";

class StorageManager {
    _storage;

    constructor(typeOfStorage: any | WindowSessionStorage) {
        this._storage = typeOfStorage;
    }

    removeItem(key: string) {
        this._storage.removeItem(key);
    }

    setItem(key: string, value: any) {
        this._storage.setItem(key, value);
    }

    setItemAfterSerializing(key: string, value: any) {
        this.setItem(key, JSON.stringify(value));
    }

    getItem(key: string, defaultValue = null) {
        const storedValue = this._storage.getItem(key);
        return storedValue == null ? defaultValue : storedValue;
    }

    getParsedItem(key: string, defaultValue = null) {
        const storedValue = this._storage.getItem(key);
        try {
            return storedValue == null ? defaultValue : JSON.parse(storedValue);
        } catch {
            console.error(`Error parsing ${key}: ${storedValue}`);
            return defaultValue;
        }
    }

    setBooleanValue(key: string, value: any) {
        this.setItem(key, value === true ? "true" : "false");
    }

    getBooleanValue(key: string) {
        const value = this.getItem(key);
        return value != null && value === "true";
    }

    contains(key: string) {
        return this.getItem(key) != null;
    }
}

export const storage = new StorageManager(window.localStorage);

export const sessionStorage = new StorageManager(window.sessionStorage);

export class CachedItem {
    _key;
    _cachedTimeKey;
    _shouldParse = false;
    _timeToLiveInSeconds = 0;
    _storage;

    constructor(key: string, shouldParse = true, timeToLiveInSeconds = 0, usePrefix = true, useLocalStorage = true) {
        this._key = usePrefix ? `${cachedItemPrefix}.${key}` : key;
        this._cachedTimeKey = `${this._key}.cached.time`;
        this._shouldParse = shouldParse;
        this._timeToLiveInSeconds = timeToLiveInSeconds;
        this._storage = useLocalStorage ? storage : sessionStorage;
    }

    static withoutPrefix(key: string, shouldParse = true, timeToLiveInSeconds = 0, useLocalStorage = true) {
        return new CachedItem(key, shouldParse, timeToLiveInSeconds, false, useLocalStorage);
    }

    private _getExpiry() {
        let cachedTime = storage.getItem(this._cachedTimeKey);
        cachedTime = cachedTime === null ? 0 : parseInt(cachedTime);

        return cachedTime === 0 ? 0 : cachedTime + (this._timeToLiveInSeconds * 1000);
    }

    isExpired() {
        if (this._timeToLiveInSeconds === 0) {
            return false;
        }

        const expiry = this._getExpiry();

        const time = new Date().getTime();

        return expiry === 0 || expiry < time;
    }

    get isCached() {
        const cached = this._storage.contains(this._key);
        return cached && !this.isExpired();
    }

    clear() {
        // console.log(`Removing ${this._key}`);
        this._storage.removeItem(this._key);
        this._storage.removeItem(this._cachedTimeKey);
    }

    get value() {
        return this.get();
    }

    get() {
        return this._shouldParse
            ? this._storage.getParsedItem(this._key)
            : this._storage.getItem(this._key);
    }

    set(value: any) {
        const time = new Date().getTime();

        this._shouldParse
            ? this._storage.setItemAfterSerializing(this._key, value)
            : this._storage.setItem(this._key, value);

        if (this._timeToLiveInSeconds === 0) {
            return;
        }

        this._storage.setItem(this._cachedTimeKey, time);
    }
}

export class CachedBooleanValue extends CachedItem {
    constructor(key: string, timeToLive = 0, usePrefix = true, useLocalStorage = true) {
        super(key, false, timeToLive, usePrefix, useLocalStorage);
    }

    override get() {
        return this._storage.getBooleanValue(this._key);
    }

    override set(value: any) {
        this._storage.setBooleanValue(this._key, value);
    }
}

export default { CachedBooleanValue, CachedItem }